/**
 * UseCasesSection Component
 * Displays real-world use cases relevant to the prospect's industry.
 * Enterprise tier - can be populated from the Use Cases Project doc
 * or Fireflies context.
 */

import React, { useState } from 'react';

const DEFAULT_USE_CASES = [
  {
    id: 'uc-1',
    industry: 'Construction & Infrastructure',
    title: 'Remote Site Connectivity',
    challenge: 'Construction sites in regional areas with no fixed-line infrastructure, needing reliable internet for BIM software, CCTV monitoring, and daily reporting.',
    solution: 'Starlink Business + 5G failover delivers 150+ Mbps to any site. Paired with solar-powered CCTV and Talk Australia managed network.',
    outcome: 'Sites operational in 48 hours with enterprise-grade connectivity. 60% cost reduction vs traditional satellite.',
    tags: ['Starlink', 'CCTV', '5G Failover', 'Regional'],
  },
  {
    id: 'uc-2',
    industry: 'Professional Services',
    title: 'Unified Communications Rollout',
    challenge: 'Law firm with 3 offices using separate phone systems, Teams, and personal mobiles — no unified presence or call routing.',
    solution: 'Microsoft Teams Direct Routing with Talk Australia SIP. Single identity across desk, mobile, and laptop with intelligent call routing.',
    outcome: 'One phone system across all offices. 40% reduction in telco spend. Full call recording for compliance.',
    tags: ['UCaaS', 'Teams', 'SIP', 'Multi-Site'],
  },
  {
    id: 'uc-3',
    industry: 'Fleet & Logistics',
    title: 'Fleet Visibility & Dash Cams',
    challenge: 'Transport company with 80+ vehicles and no real-time tracking, driver behaviour monitoring, or incident footage.',
    solution: 'Integrated dash cam solution with GPS tracking, driver scorecards, real-time alerts, and cloud-stored footage accessible from portal.',
    outcome: 'Insurance premiums reduced 25%. Incident disputes resolved in hours, not weeks. Full fleet visibility from Talk Australia portal.',
    tags: ['Dash Cams', 'GPS', 'Fleet', 'IoT'],
  },
  {
    id: 'uc-4',
    industry: 'Education',
    title: 'Campus-Wide Network Upgrade',
    challenge: 'School with ageing Wi-Fi, no BYOD policy enforcement, and parents demanding better digital tools for learning.',
    solution: 'Enterprise Wi-Fi 6E deployment with segmented VLANs (student, staff, IoT), content filtering, and managed endpoint security.',
    outcome: '3x Wi-Fi capacity. Zero unplanned outages in 12 months. Full compliance with eSafety Commissioner guidelines.',
    tags: ['Wi-Fi', 'Security', 'Education', 'BYOD'],
  },
];

const styles = {
  section: {
    padding: '60px 20px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    textAlign: 'center',
    marginBottom: '48px',
  },
  badge: {
    display: 'inline-block',
    padding: '6px 16px',
    backgroundColor: 'rgba(0, 212, 170, 0.15)',
    color: '#00D4AA',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '600',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    marginBottom: '16px',
  },
  title: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: '12px',
  },
  subtitle: {
    fontSize: '16px',
    color: '#8B92A5',
    maxWidth: '600px',
    margin: '0 auto',
    lineHeight: '1.6',
  },
  cardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  card: {
    backgroundColor: '#1A2332',
    borderRadius: '12px',
    border: '1px solid #2A3444',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
  },
  cardExpanded: {
    borderColor: '#00D4AA',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px 28px',
    cursor: 'pointer',
  },
  cardHeaderLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  industry: {
    fontSize: '12px',
    color: '#00D4AA',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  cardTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#FFFFFF',
  },
  chevron: {
    fontSize: '20px',
    color: '#8B92A5',
    transition: 'transform 0.3s ease',
  },
  chevronOpen: {
    transform: 'rotate(180deg)',
  },
  cardBody: {
    padding: '0 28px 28px',
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '20px',
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  columnLabel: {
    fontSize: '11px',
    fontWeight: '700',
    color: '#5B9DFF',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  columnText: {
    fontSize: '14px',
    color: '#8B92A5',
    lineHeight: '1.6',
  },
  tags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '16px',
    paddingLeft: '28px',
    paddingBottom: '20px',
  },
  tag: {
    padding: '4px 10px',
    backgroundColor: 'rgba(0, 87, 255, 0.12)',
    color: '#5B9DFF',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '500',
  },
};

function UseCasesSection({ prospect }) {
  const [expandedCards, setExpandedCards] = useState(new Set([0]));

  const useCases = prospect?.useCases?.length > 0
    ? prospect.useCases
    : DEFAULT_USE_CASES;

  const toggleCard = (index) => {
    setExpandedCards((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  return (
    <div style={styles.section}>
      <div style={styles.header}>
        <div style={styles.badge}>Proven Results</div>
        <h2 style={styles.title}>Real-World Use Cases</h2>
        <p style={styles.subtitle}>
          See how businesses like {prospect?.companyName || 'yours'} have transformed their operations
          with Talk Australia.
        </p>
      </div>

      <div style={styles.cardList}>
        {useCases.map((useCase, index) => {
          const isExpanded = expandedCards.has(index);
          return (
            <div
              key={useCase.id || index}
              style={{
                ...styles.card,
                ...(isExpanded ? styles.cardExpanded : {}),
              }}
            >
              <div style={styles.cardHeader} onClick={() => toggleCard(index)}>
                <div style={styles.cardHeaderLeft}>
                  <span style={styles.industry}>{useCase.industry}</span>
                  <span style={styles.cardTitle}>{useCase.title}</span>
                </div>
                <span style={{
                  ...styles.chevron,
                  ...(isExpanded ? styles.chevronOpen : {}),
                }}>
                  \u25BC
                </span>
              </div>
              {isExpanded && (
                <>
                  <div style={styles.cardBody}>
                    <div style={styles.column}>
                      <span style={styles.columnLabel}>Challenge</span>
                      <span style={styles.columnText}>{useCase.challenge}</span>
                    </div>
                    <div style={styles.column}>
                      <span style={styles.columnLabel}>Solution</span>
                      <span style={styles.columnText}>{useCase.solution}</span>
                    </div>
                    <div style={styles.column}>
                      <span style={styles.columnLabel}>Outcome</span>
                      <span style={styles.columnText}>{useCase.outcome}</span>
                    </div>
                  </div>
                  {useCase.tags && (
                    <div style={styles.tags}>
                      {useCase.tags.map((tag) => (
                        <span key={tag} style={styles.tag}>{tag}</span>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default UseCasesSection;
